/**
 * AuthCallback - Handles Google OAuth redirect
 * Exchanges the ?code= authorization code delivered via URL query string
 */
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Loader2 } from 'lucide-react';

const AuthCallback = () => {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Use ref to prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Google OAuth flow: code/state delivered via URL query string
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        const state = params.get('state');
        const oauthError = params.get('error');

        if (oauthError) {
          // User cancelled Google's consent screen or Google returned an error
          console.error('Google OAuth error:', oauthError);
          navigate('/login', { replace: true });
          return;
        }
        if (!code) {
          console.error('No code found in URL');
          navigate('/login', { replace: true });
          return;
        }

        const response = await api.googleCallback(code, state);
        if (!response.data?.success) {
          throw new Error('Session exchange failed');
        }

        // The callback above only exchanges the code and establishes the
        // session cookie server-side - it does NOT hydrate app state.
        // Hydrate through checkAuth(), the same canonical path every other
        // read of auth state (ProtectedRoute, the dev-login button, the
        // AuthProvider mount effect) already uses, so this path can't
        // diverge from them on any field - consent_given included.
        const authState = await checkAuth();
        if (!authState.isAuthenticated) {
          throw new Error('Session exchange failed');
        }

        // First-time users land on About ProcMatrix instead of the Dashboard.
        // beta_procurement_count alone isn't sufficient - it's never
        // incremented for owner accounts - so we also check for any
        // existing procurements before treating this as a first login.
        let isFirstLogin = (authState.betaProcurementCount || 0) === 0;
        if (isFirstLogin) {
          try {
            // ENVELOPE, NOT A BARE LIST (2026-09-01). `total` is the right
            // field here, not items.length: one page can be empty while the
            // user owns plenty, which would send a returning user to /about.
            const procurementsResponse = await api.getAllProcurements({ pageSize: 25 });
            isFirstLogin = (procurementsResponse.data?.total || 0) === 0;
          } catch (procurementsError) {
            console.error('Failed to check existing procurements:', procurementsError);
            isFirstLogin = false; // fall back to dashboard on error
          }
        }

        navigate(isFirstLogin ? '/about' : '/', { replace: true, state: { user: authState.user } });
      } catch (error) {
        console.error('Auth callback error:', error);
        // The callback can now refuse a session outright (an account past
        // its beta window returns 403 BETA_EXPIRED). Without this the user
        // is bounced back to /login with no explanation and no way to tell
        // a closed account from a transient failure.
        const refusal = error?.response?.data?.detail;
        navigate('/login', {
          replace: true,
          state: refusal?.code ? { authError: refusal.code, authErrorMessage: refusal.message } : undefined,
        });
      }
    };

    processAuth();
  }, [navigate, checkAuth]);

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#0055FF] mx-auto mb-4" />
        <p className="text-zinc-600">Completing sign in...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
